import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, LockKey, CheckCircle } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";

/**
 * ForgotPassword — dual-mode page:
 *   • no `?token=` in URL → step 1: enter email, we send a reset link
 *   • `?token=XYZ` in URL → step 2: enter a new password, we complete reset
 */
export default function ForgotPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ResetStep token={token} /> : <RequestStep />;
}

function RequestStep() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-3" data-testid="forgot-sent">
        <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-400" />
        <h1 className="font-display font-black text-2xl">Check your inbox</h1>
        <p className="text-sm text-zinc-400 leading-relaxed">
          If <span className="text-white">{email}</span> is registered with Hunt57, we&apos;ve sent a password reset link.
          It expires in 60 minutes and can only be used once.
        </p>
        <p className="text-xs text-zinc-500">Not in your inbox? Check your spam folder.</p>
        <Link to="/login" data-testid="forgot-back-login" className="inline-block h57-btn-secondary mt-4">Back to sign in</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-4 py-16 space-y-4" data-testid="forgot-request">
      <div className="text-center space-y-1">
        <EnvelopeSimple size={40} weight="duotone" className="mx-auto text-[#FFD700]" />
        <h1 className="font-display font-black text-2xl">Reset your password</h1>
        <p className="text-sm text-zinc-400">Enter your email — we&apos;ll send you a reset link.</p>
      </div>
      <div>
        <label className="h57-label">Email</label>
        <input
          type="email"
          required
          className="h57-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="forgot-email-input"
          placeholder="you@example.com"
        />
      </div>
      <button type="submit" disabled={busy || !email} data-testid="forgot-submit-btn" className="h57-btn-primary w-full">
        {busy ? "Sending…" : "Send reset link"}
      </button>
      <div className="text-center">
        <Link to="/login" className="text-xs text-zinc-500 hover:text-white">Back to sign in</Link>
      </div>
    </form>
  );
}

function ResetStep({ token }) {
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don\u2019t match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Sign in to continue.");
      navigate("/login");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-4 py-16 space-y-4" data-testid="reset-form">
      <div className="text-center space-y-1">
        <LockKey size={40} weight="duotone" className="mx-auto text-[#FFD700]" />
        <h1 className="font-display font-black text-2xl">Set a new password</h1>
        <p className="text-sm text-zinc-400">Choose a new password for your Hunt57 account.</p>
      </div>
      <div>
        <label className="h57-label">New password</label>
        <input type="password" required minLength={6} className="h57-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password" />
      </div>
      <div>
        <label className="h57-label">Confirm new password</label>
        <input type="password" required minLength={6} className="h57-input" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-password" />
      </div>
      <button type="submit" disabled={busy || !pw || !pw2} data-testid="reset-submit-btn" className="h57-btn-primary w-full">
        {busy ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}
